import React, { JSX, useMemo, useState } from 'react';

import { Box, Chip, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import { LatLng } from 'leaflet';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Types
import { VisitLogEntry } from '../../../types/VisitLogEntry';
import { useVisitGeofences } from '../../../hooks/useVisitGeofences';

// Sub-components
import VisitJourneyMap, { UniqueFacility, UniqueReader } from './VisitJourneyMap/VisitJourneyMap';
import VisitSightingsPanel from './VisitJourneyMap/VisitSightingsPanel';
import VisitJourneyPanel, { JourneyFacility } from './VisitJourneyMap/VisitJourneyPanel';

const MAP_HEIGHT = 580;

interface VisitReplayMapProps {
  visitId: string;
  deviceLocation?: { latitude: number; longitude: number } | null;
  canViewSightings?: boolean;
}

// VisitJourneyMap can render one dot per facility on the left map.
// When a centroid lookup is provided, trail points snap to the geofence centroid
// of the facility instead of the raw sighting location.
const toMapEntries = (
  facilities: JourneyFacility[],
  visitId: string,
  centroidByFacilityId?: Map<string, { latitude: number; longitude: number }>,
): VisitLogEntry[] =>
  facilities
    .map((f) => {
      const centroid = centroidByFacilityId?.get(f.facilityId);
      const loc = centroid ?? f.location;
      if (loc?.latitude == null) return null;
      return {
        id: f.rowId ?? f.facilityId,
        deviceId: '',
        facilityId: f.facilityId,
        facilityName: f.facilityName,
        closestReaderImei: '',
        closestReaderName: f.facilityName,
        firstSeenDateTime: f.firstSeenDateTime,
        lastSeenDateTime: f.lastSeenDateTime,
        rssi: 0,
        temperatureF: f.averageTemperatureF,
        temperatureC: f.averageTemperatureC,
        humidity: 0,
        batteryPercent: 0,
        location: loc,
        locationMethodDescr: f.locationMethodDescr ?? '',
        visitId,
      } as VisitLogEntry;
    })
    .filter((e): e is VisitLogEntry => e !== null);

export default function VisitReplayMap({ visitId, deviceLocation, canViewSightings = true }: VisitReplayMapProps): JSX.Element | null {
  const visitIdStr = typeof visitId === 'string' ? visitId.trim() : String(visitId).trim();

  // Active visit journey state
  const [activeVisitFacilities, setActiveVisitFacilities] = useState<JourneyFacility[]>([]);
  const [activeVisitActiveIdx, setActiveVisitActiveIdx] = useState<number | null>(null);
  const [activeVisitLoading, setActiveVisitLoading] = useState(false);
  const [activeVisitError, setActiveVisitError] = useState<string | null>(null);

  // Sightings slide-over for the active visit journey
  const [showActiveVisitSightings, setShowActiveVisitSightings] = useState(false);
  const [activeVisitSightingsPanelVisible, setActiveVisitSightingsPanelVisible] = useState(false);
  const [activeVisitSelectedFacilityId, setActiveVisitSelectedFacilityId] = useState<string | null>(null);
  const [activeVisitSelectedFacilityLogId, setActiveVisitSelectedFacilityLogId] = useState<string | null>(null);
  const [activeVisitSelectedFacilityFirstSeen, setActiveVisitSelectedFacilityFirstSeen] = useState<string | null>(null);
  const [activeVisitSelectedFacilityLastSeen, setActiveVisitSelectedFacilityLastSeen] = useState<string | null>(null);
  const [activeVisitPanelData, setActiveVisitPanelData] = useState<VisitLogEntry[]>([]);
  const [activeVisitSightingsActiveIdx, setActiveVisitSightingsActiveIdx] = useState<number | null>(null);
  const [activeVisitHoveredIdx, setActiveVisitHoveredIdx] = useState<number | null>(null);

  const handleActiveVisitViewSightings = (facilityId: string, firstSeenDateTime: string, lastSeenDateTime: string, closestFacilityLogId: string) => {
    if (!canViewSightings) return;

    setActiveVisitSelectedFacilityId(facilityId);
    setActiveVisitSelectedFacilityLogId(closestFacilityLogId || null);
    setActiveVisitSelectedFacilityFirstSeen(firstSeenDateTime);
    setActiveVisitSelectedFacilityLastSeen(lastSeenDateTime);
    setActiveVisitSightingsActiveIdx(null);
    setActiveVisitPanelData([]);
    setShowActiveVisitSightings(true);
    requestAnimationFrame(() => setActiveVisitSightingsPanelVisible(true));
  };

  const handleActiveVisitBackFromSightings = () => {
    setActiveVisitSightingsPanelVisible(false);
    setActiveVisitSightingsActiveIdx(null);
    setTimeout(() => {
      setShowActiveVisitSightings(false);
      setActiveVisitSelectedFacilityId(null);
      setActiveVisitSelectedFacilityLogId(null);
      setActiveVisitSelectedFacilityFirstSeen(null);
      setActiveVisitSelectedFacilityLastSeen(null);
      setActiveVisitPanelData([]);
    }, 300);
  };

  // Anchor geofence fetch on the device location if provided, otherwise fall
  // back to the first journey facility that has a location.
  const geofenceAnchorLocation = useMemo(() => {
    if (deviceLocation?.latitude != null) return deviceLocation;
    const withLoc = activeVisitFacilities.find((f) => f.location?.latitude != null);
    return withLoc?.location ?? null;
  }, [deviceLocation, activeVisitFacilities]);

  // Fetch + colour-tier facility geofences for the area around the device.
  const { geofences, centroidByFacilityId } = useVisitGeofences({
    location: geofenceAnchorLocation,
    journeyFacilities: activeVisitFacilities,
  });

  // Active visit map computations
  const activeVisitMapData = useMemo(
    () => toMapEntries(activeVisitFacilities, visitId ?? '', centroidByFacilityId),
    [activeVisitFacilities, visitId, centroidByFacilityId],
  );

  const activeVisitExactCoords = useMemo<[number, number][]>(
    () => activeVisitMapData.map((e) => [e.location.latitude, e.location.longitude]),
    [activeVisitMapData],
  );

  const activeVisitCenter = useMemo<LatLng>(
    () =>
      activeVisitMapData.length > 0
        ? new LatLng(activeVisitMapData[0].location.latitude, activeVisitMapData[0].location.longitude)
        : new LatLng(0, 0),
    [activeVisitMapData],
  );

  const activeVisitActivePosition = useMemo<LatLng | null>(() => {
    if (activeVisitActiveIdx === null || !activeVisitMapData[activeVisitActiveIdx]) return null;
    return new LatLng(activeVisitMapData[activeVisitActiveIdx].location.latitude, activeVisitMapData[activeVisitActiveIdx].location.longitude);
  }, [activeVisitActiveIdx, activeVisitMapData]);

  const activeVisitUniqueFacilities = useMemo<UniqueFacility[]>(
    () =>
      activeVisitMapData.map((e) => {
        const pad = 0.0008;
        const lat = e.location.latitude;
        const lng = e.location.longitude;
        return {
          lat,
          lng,
          name: e.facilityName ?? '',
          bounds: [
            [lat + pad, lng - pad],
            [lat + pad, lng + pad],
            [lat - pad, lng + pad],
            [lat - pad, lng - pad],
          ] as [number, number][],
        };
      }),
    [activeVisitMapData],
  );

  // Sightings map computations for active visit sightings
  const activeVisitSightingsExactCoords = useMemo<[number, number][]>(
    () => activeVisitPanelData.map((e) => [e.location.latitude, e.location.longitude]),
    [activeVisitPanelData],
  );

  const activeVisitSightingsSpreadPositions = useMemo<[number, number][]>(() => {
    const groups: Record<string, number[]> = {};
    activeVisitPanelData.forEach((e, i) => {
      const key = `${e.location.latitude.toFixed(6)},${e.location.longitude.toFixed(6)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(i);
    });
    const result: [number, number][] = new Array(activeVisitPanelData.length);
    Object.values(groups).forEach((indices) => {
      const n = indices.length;
      indices.forEach((idx, j) => {
        const e = activeVisitPanelData[idx];
        if (n === 1) {
          result[idx] = [e.location.latitude, e.location.longitude];
        } else {
          const angle = (j / n) * 2 * Math.PI - Math.PI / 2;
          const r = 0.00015;
          result[idx] = [
            e.location.latitude + r * Math.cos(angle),
            e.location.longitude + (r * Math.sin(angle)) / Math.cos((e.location.latitude * Math.PI) / 180),
          ];
        }
      });
    });
    return result;
  }, [activeVisitPanelData]);

  const activeVisitSightingsCenter = useMemo<LatLng>(
    () =>
      activeVisitPanelData.length > 0
        ? new LatLng(activeVisitPanelData[0].location.latitude, activeVisitPanelData[0].location.longitude)
        : new LatLng(0, 0),
    [activeVisitPanelData],
  );

  const activeVisitSightingsActivePosition = useMemo<LatLng | null>(() => {
    if (activeVisitSightingsActiveIdx === null || !activeVisitPanelData[activeVisitSightingsActiveIdx]) return null;
    return new LatLng(
      activeVisitPanelData[activeVisitSightingsActiveIdx].location.latitude,
      activeVisitPanelData[activeVisitSightingsActiveIdx].location.longitude,
    );
  }, [activeVisitSightingsActiveIdx, activeVisitPanelData]);

  const activeVisitSightingsUniqueReaders = useMemo<UniqueReader[]>(() => {
    const seen = new Map<string, UniqueReader>();
    activeVisitPanelData.forEach((e) => {
      const key = `${e.location.latitude.toFixed(6)},${e.location.longitude.toFixed(6)}`;
      if (!seen.has(key))
        seen.set(key, { lat: e.location.latitude, lng: e.location.longitude, name: e.closestReaderName, imei: e.closestReaderImei });
    });
    return Array.from(seen.values());
  }, [activeVisitPanelData]);

  const activeVisitSightingsUniqueFacilities = useMemo<UniqueFacility[]>(() => {
    const seen = new Map<string, UniqueFacility>();
    activeVisitPanelData.forEach((e) => {
      if (!e.facilityName || seen.has(e.facilityName)) return;
      const lat = e.location.latitude;
      const lng = e.location.longitude;
      const pad = 0.0008;
      seen.set(e.facilityName, {
        lat,
        lng,
        name: e.facilityName,
        bounds: [
          [lat + pad, lng - pad],
          [lat + pad, lng + pad],
          [lat - pad, lng + pad],
          [lat - pad, lng - pad],
        ],
      });
    });
    return Array.from(seen.values());
  }, [activeVisitPanelData]);

  // guard
  if (!visitIdStr || visitIdStr === '[object Object]') {
    console.error('Invalid visitId passed to VisitReplayMap:', visitId);
    return null;
  }

  return (
    <>
      {visitIdStr && (
        <Paper
          elevation={0}
          sx={{
            border: 'none',
            borderRadius: 3,
            overflowX: 'hidden',
            overflowY: 'visible',
            mt: 1,
            bgcolor: '#F3F5F8',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              position: 'relative',
              zIndex: 1,
              backgroundColor: 'transparent',
            }}
          >
            <Typography variant='subtitle2' fontWeight={700} sx={{ color: '#111827' }}>
              Visit Journey
            </Typography>
            <Chip label='Active Visit' size='small' sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700, fontSize: 11 }} />
            {activeVisitLoading && <CircularProgress size={14} sx={{ color: '#334155' }} />}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: MAP_HEIGHT, position: 'relative', zIndex: 2 }}>
            {/* Left: facility-level map */}
            <Box sx={{ flex: 1, borderRight: '1px solid', borderColor: 'divider', height: '100%', position: 'relative' }}>
              {showActiveVisitSightings ? (
                // Sightings level map
                activeVisitPanelData.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                    <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
                    <Typography variant='caption' color='text.secondary'>
                      Loading sightings...
                    </Typography>
                  </Box>
                ) : (
                  <VisitJourneyMap
                    filtered={activeVisitPanelData}
                    exactCoords={activeVisitSightingsExactCoords}
                    spreadPositions={activeVisitSightingsSpreadPositions}
                    center={activeVisitSightingsCenter}
                    activePosition={activeVisitSightingsActivePosition}
                    activeIdx={activeVisitSightingsActiveIdx}
                    hoveredIdx={activeVisitHoveredIdx}
                    uniqueReaders={activeVisitSightingsUniqueReaders}
                    uniqueFacilities={activeVisitSightingsUniqueFacilities}
                    geofences={geofences}
                    onMarkerClick={setActiveVisitSightingsActiveIdx}
                    onMarkerHover={setActiveVisitHoveredIdx}
                  />
                )
              ) : activeVisitError ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {activeVisitError}
                  </Typography>
                </Box>
              ) : activeVisitLoading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                  <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
                  <Typography variant='caption' color='text.secondary'>
                    Loading journey...
                  </Typography>
                </Box>
              ) : activeVisitMapData.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                  <Typography variant='body2' color='text.secondary'>
                    No mappable location for this journey.
                  </Typography>
                </Box>
              ) : (
                <VisitJourneyMap
                  filtered={activeVisitMapData}
                  exactCoords={activeVisitExactCoords}
                  spreadPositions={activeVisitExactCoords}
                  center={activeVisitCenter}
                  activePosition={activeVisitActivePosition}
                  activeIdx={activeVisitActiveIdx}
                  hoveredIdx={null}
                  uniqueReaders={[]}
                  uniqueFacilities={activeVisitUniqueFacilities}
                  geofences={geofences}
                  onMarkerClick={setActiveVisitActiveIdx}
                  onMarkerHover={() => void 0}
                />
              )}
            </Box>

            {/* Right: journey panel + sightings slide-over */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                overflow: 'visible',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Journey panel */}
              <VisitJourneyPanel
                visitId={visitId}
                activeIdx={activeVisitActiveIdx}
                canViewSightings={canViewSightings}
                showRaisedStats={!showActiveVisitSightings}
                onViewSightings={handleActiveVisitViewSightings}
                onFacilitiesChange={setActiveVisitFacilities}
                onFocusMarker={setActiveVisitActiveIdx}
                onLoadingChange={setActiveVisitLoading}
                onErrorChange={setActiveVisitError}
              />

              {/* Sightings slide-over */}
              {showActiveVisitSightings && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    ml: '10px',
                    boxSizing: 'border-box',
                    border: '2px solid',
                    borderColor: '#dfe6ef',
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      boxSizing: 'border-box',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      transform: activeVisitSightingsPanelVisible ? 'translateX(0)' : 'translateX(100%)',
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '-4px 0 20px rgba(15, 23, 42, 0.12)',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#f8fafc',
                        flexShrink: 0,
                      }}
                    >
                      <IconButton size='small' onClick={handleActiveVisitBackFromSightings} sx={{ color: '#334155' }}>
                        <ArrowBackIcon fontSize='small' />
                      </IconButton>
                      <Typography variant='subtitle2' fontWeight={700} sx={{ color: '#111827' }}>
                        Visit Sightings
                      </Typography>
                      <Typography variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                        {visitId}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <VisitSightingsPanel
                        visitId={visitId}
                        facilityId={activeVisitSelectedFacilityId}
                        closestFacilityLogId={activeVisitSelectedFacilityLogId}
                        firstSeenDateTime={activeVisitSelectedFacilityFirstSeen}
                        lastSeenDateTime={activeVisitSelectedFacilityLastSeen}
                        activeIdx={activeVisitSightingsActiveIdx}
                        onActiveIdxChange={setActiveVisitSightingsActiveIdx}
                        onDataChange={setActiveVisitPanelData}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      )}
    </>
  );
}
